import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueueEntry } from '@his/shared';
import { del, get, post, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useWorkingBranchId } from '@/lib/branch';
import { fallbackInterval, useRealtimeInvalidate } from '@/lib/realtime';
import { humanize } from '@/lib/utils';
import { DataTable, FilterBar, FilterField, PageHeader } from '@/components/ui/layout';
import { Alert, Badge, Button, Select, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { VitalsDialog } from '@/components/VitalsDialog';
import { queueNewbornFacts } from './emr/completion';

interface DoctorOption {
  id: string;
  name: string;
}

export function TriagePage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const branchId = useWorkingBranchId();
  const [doctorId, setDoctorId] = useState('');
  const [takingVitals, setTakingVitals] = useState<QueueEntry | null>(null);

  const doctors = useQuery({
    queryKey: ['queue-doctors', branchId],
    queryFn: () => get<DoctorOption[]>(`/queue/doctors${qs({ branchId })}`),
    enabled: Boolean(branchId),
  });

  const live = useRealtimeInvalidate(branchId ? [`queue-branch:${branchId}`] : [], null, [['queue']]);
  const queue = useQuery({
    queryKey: ['queue', doctorId, 'triage'],
    queryFn: () => get<QueueEntry[]>(`/queue${qs({ doctorId: doctorId || undefined, allDoctors: doctorId ? undefined : true, branchId })}`),
    enabled: Boolean(branchId),
    refetchInterval: fallbackInterval(live, 60_000),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['queue'] });

  const ready = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) => (on ? post(`/appointments/${id}/ready`) : del(`/appointments/${id}/ready`)),
    onSuccess: refresh,
    onError: (error) => toast.error(tr('opd', 'could_not_change_the_hand_off'), describeError(error)),
  });

  const priority = useMutation({
    mutationFn: ({ id, urgent }: { id: string; urgent: boolean }) =>
      put(`/appointments/${id}/priority`, urgent ? { priority: 'urgent', priorityReason: 'clinical' } : { priority: 'routine' }),
    onSuccess: refresh,
    onError: (error) => toast.error(tr('opd', 'could_not_change_the_priority'), describeError(error)),
  });

  const callNext = useMutation({
    mutationFn: () => post<{ tokenNo: string | null }>('/queue/call-next', { doctorId }),
    onSuccess: (called) => {
      toast.success(called.tokenNo ? tr('opd', 'token_x_called', { tokenNo: called.tokenNo }) : tr('opd', 'next_patient_called'));
      refresh();
    },
    onError: (error) => toast.error(tr('opd', 'nobody_to_call'), describeError(error)),
  });

  const waiting = (queue.data ?? []).filter((row) => ['arrived', 'in_consultation'].includes(row.status));

  return (
    <>
      <PageHeader
        title={tr('opd', 'triage')}
        description={tr('opd', 'vitals_screening_and_hand_off_to_the_doctor')}
        actions={
          doctorId && (
            <Button variant="outline" onClick={() => callNext.mutate()} loading={callNext.isPending}>
              {tr('opd', 'call_next')}
            </Button>
          )
        }
      />

      {!branchId && <Alert variant="warning">{tr('opd', 'your_account_has_no_branch_ask_an_administrator')}</Alert>}

      <FilterBar>
        <FilterField label={tr('opd', 'doctor')}>
          <Select value={doctorId} onChange={(e) => setDoctorId(e.target.value)} className="w-56">
            <option value="">{tr('opd', 'all_doctors')}</option>
            {doctors.data?.map((doctor) => (
              <option key={doctor.id} value={doctor.id}>
                {doctor.name}
              </option>
            ))}
          </Select>
        </FilterField>
      </FilterBar>

      <DataTable
        rows={waiting}
        loading={queue.isLoading} refreshing={queue.isFetching}
        error={queue.error}
        rowKey={(row) => row.appointmentId}
        empty={tr('opd', 'nobody_is_waiting_for_triage')}
        columns={[
          { header: tr('opd', 'token'), width: '80px', sort: (row) => row.tokenNo, cell: (row) => <span className="tabular text-base font-semibold">{row.tokenNo ?? '—'}</span> },
          {
            header: tr('opd', 'patient'),
            sort: (row) => row.patientName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.patientName}</p>
                <p className="text-xs text-muted-foreground">
                  {row.mrn} · {row.ageLabel} · {humanize(row.sex, 'sex')}
                </p>
              </div>
            ),
          },
          {
            header: tr('opd', 'stage'),
            cell: (row) => (
              <div className="flex flex-wrap gap-1">
                <StatusBadge status={row.status} />
                {row.stage === 'ready' && <Badge variant="success">{tr('opd', 'ready_2')}</Badge>}
                {row.stage === 'called' && <Badge variant="secondary">{tr('opd', 'called')}</Badge>}
                {row.priority === 'urgent' && <Badge variant="destructive">{tr('opd', 'see_first')}</Badge>}
                {row.abnormalVitals && <Badge variant="warning">{tr('opd', 'abnormal_vitals')}</Badge>}
                <Badge variant={row.hasVitals ? 'success' : 'muted'}>{row.hasVitals ? tr('opd', 'vitals') : tr('opd', 'no_vitals')}</Badge>
                <Badge variant={row.hasScreening ? 'success' : 'muted'}>{row.hasScreening ? tr('opd', 'screened') : tr('opd', 'not_screened')}</Badge>
              </div>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              row.status === 'arrived' &&
              can('vitals.write') && (
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => setTakingVitals(row)}>
                    {tr('opd', 'vitals_2')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/triage/${row.appointmentId}/screening`)}>
                    {tr('opd', 'screening')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => priority.mutate({ id: row.appointmentId, urgent: row.priority !== 'urgent' })}>
                    {row.priority === 'urgent' ? tr('opd', 'routine') : tr('opd', 'see_first_2')}
                  </Button>
                  <Button size="sm" onClick={() => ready.mutate({ id: row.appointmentId, on: !row.readyAt })}>
                    {row.readyAt ? tr('opd', 'not_ready') : tr('opd', 'ready')}
                  </Button>
                </div>
              ),
          },
        ]}
      />

      {takingVitals && (
        <VitalsDialog
          open
          onClose={() => setTakingVitals(null)}
          patientId={takingVitals.patientId}
          patientName={`${takingVitals.patientName} · ${takingVitals.mrn}`}
          appointmentId={takingVitals.appointmentId}
          invalidateKeys={[['queue']]}
          recheck={takingVitals.hasVitals}
          newborn={queueNewbornFacts(takingVitals)}
        />
      )}
    </>
  );
}
